import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  BellFilled,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  message as messageAlert,
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";
import {
  createNotificationAdminAlert,
  deleteNotificationAdminAlert,
  getNotificationsAdminAlert,
  getSchedulerHealthAdminAlert,
  updateNotificationAdminAlert,
} from "../../RestServices/authService";
import "./body.css";

const { TextArea } = Input;
const { Title, Text } = Typography;

// Pakistan Standard Time — fixed +05:00 offset, no DST.
// moment-timezone isn't available in this project, so we anchor moments via
// .utcOffset() / parseZone(). PKT doesn't switch offset during the year so
// this is exact, not an approximation.
const PKT_OFFSET = "+05:00";
const PKT_LABEL = "Asia/Karachi (PKT, UTC+5)";

// Interpret a stored sentTime (ISO/UTC) as a moment anchored in PKT so the
// display matches what the backend will fire at.
const toPKT = (isoOrMoment) =>
  moment.parseZone(moment(isoOrMoment).utcOffset(PKT_OFFSET).format());

// Build a PKT-anchored moment from the admin's picked Y/M/D/H/M components.
// CRITICAL: the admin sees pickers labelled "(PKT)" and types numbers
// intending PKT wall clock. AntD pickers return moments in browser-local TZ,
// but we don't care about the browser's TZ at all — we treat the H/M numbers
// the admin typed as PKT directly. This keeps the scheduler firing at the
// right wall clock regardless of where the admin's browser thinks it is
// (PKT, UTC, US/Pacific, etc).
const buildPKTMoment = (date, time) => {
  if (!date || !time) return null;
  const ymd = `${date.format("YYYY")}-${date.format("MM")}-${date.format("DD")}`;
  const hms = `${time.format("HH")}:${time.format("mm")}:00`;
  // parseZone preserves the +05:00 offset → format() / isBefore() / etc all
  // operate on the correct PKT instant.
  return moment.parseZone(`${ymd}T${hms}${PKT_OFFSET}`);
};

// Turn a PKT-anchored moment into the ISO string the backend expects.
// Now that the moment is parseZone(+05:00), format() emits the PKT wall
// clock and we just append the offset for clarity.
const toPKTIso = (m) => {
  if (!m) return null;
  return m.clone().format("YYYY-MM-DDTHH:mm:ss") + PKT_OFFSET;
};

// "Now" as a PKT-anchored moment — used for picker defaults and
// past-time validation so comparisons happen in PKT, not browser-local.
const nowPKT = () => toPKT(moment());

// Short helper for table rendering.
const fmtPKT = (iso) => (iso ? toPKT(iso).format("DD MMM YYYY, hh:mm A") : "—");
const fmtPKTTime = (iso) => (iso ? toPKT(iso).format("hh:mm A") : "—");

const TAB_KEYS = {
  ALL: "all",
  DAILY: "daily",
  ONE_TIME: "one_time",
  DISABLED: "disabled",
};

// Map the selected tab to the filter payload the backend now understands.
const tabToQuery = (tab) => {
  switch (tab) {
    case TAB_KEYS.DAILY:
      return { runDaily: "true", active: "true" };
    case TAB_KEYS.ONE_TIME:
      return { runDaily: "false", active: "true" };
    case TAB_KEYS.DISABLED:
      return { active: "false" };
    case TAB_KEYS.ALL:
    default:
      return {};
  }
};

const EMPTY_FORM = {
  title: "",
  message: "",
  date: null,
  time: null,
  runDaily: false,
  active: true,
  occupationTypes: [],
  businessNatures: [],
};

const VALID_TAB_KEYS = Object.values(TAB_KEYS);

const SchedulerNotification = ({ setSelected, slectedSubkey }) => {
  const { token } = useSelector((state) => state.Authentication.user);

  // List state — the active tab is mirrored into the URL (via
  // setSelected/slectedSubkey from Dashboard) so each tab has its own
  // shareable/refreshable link, e.g. /schedule-notification/daily.
  const [tab, setTab] = useState(
    VALID_TAB_KEYS.includes(slectedSubkey) ? slectedSubkey : TAB_KEYS.ALL
  );

  // Keep local tab state in sync with browser back/forward navigation.
  useEffect(() => {
    const next = VALID_TAB_KEYS.includes(slectedSubkey) ? slectedSubkey : TAB_KEYS.ALL;
    setTab((prev) => (prev === next ? prev : next));
  }, [slectedSubkey]);

  const handleTabChange = useCallback(
    (newTab) => {
      setTab(newTab);
      if (setSelected) {
        setSelected("Schedule-Notification", newTab === TAB_KEYS.ALL ? "" : newTab);
      }
    },
    [setSelected]
  );
  const [searchText, setSearchText] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  // Scheduler health — the backend exposes /schedulerHealth (503 when
  // Redis/Bull is down or the safety-net sweep isn't running) specifically
  // so this screen can warn an admin that "Schedule" will save the row but
  // nothing will actually fire. `null` = not checked yet, so we don't flash
  // a false banner before the first response lands.
  const [schedulerHealth, setSchedulerHealth] = useState(null);

  const checkSchedulerHealth = useCallback(async () => {
    if (!token) return;
    try {
      const health = await getSchedulerHealthAdminAlert(token);
      setSchedulerHealth(health);
    } catch (error) {
      // Network-level failure (not a 503 body) — treat as unknown rather
      // than healthy, but don't spam the console on every 30s poll.
      setSchedulerHealth({ healthy: false, lastError: "Health check request failed" });
    }
  }, [token]);

  useEffect(() => {
    checkSchedulerHealth();
    const interval = setInterval(checkSchedulerHealth, 30_000);
    return () => clearInterval(interval);
  }, [checkSchedulerHealth]);

  // Modal / form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // Per-row action loading: keyed by `${id}:${action}`
  const [rowBusy, setRowBusy] = useState({});

  const setBusy = useCallback((id, action, on) => {
    setRowBusy((prev) => {
      const next = { ...prev };
      const key = `${id}:${action}`;
      if (on) next[key] = true;
      else delete next[key];
      return next;
    });
  }, []);

  const isBusy = useCallback(
    (id, action) => Boolean(rowBusy[`${id}:${action}`]),
    [rowBusy]
  );

  // ── Data loading ───────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setListLoading(true);
    setListError(null);
    try {
      const query = {
        ...tabToQuery(tab),
        ...(searchText?.trim() ? { q: searchText.trim() } : {}),
        page: 1,
        limit: 100,
      };
      const response = await getNotificationsAdminAlert(query, token);
      setNotifications(response?.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setListError(
        error?.response?.data?.error ||
          "Failed to load notifications — please try again"
      );
    } finally {
      setListLoading(false);
    }
  }, [token, tab, searchText]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── Modal handlers ─────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    setEditingId(null);
    // Default to "PKT now + 1h" so the picker initial value is in the
    // same timezone as the H/M numbers we'll emit on submit. Without this
    // a UTC-browser admin would see a UTC default but submit it as PKT,
    // shifting the fire time by 5h.
    const defaultMoment = nowPKT().add(1, "hour");
    setForm({
      ...EMPTY_FORM,
      date: defaultMoment.clone(),
      time: defaultMoment.clone(),
    });
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((n) => {
    const sentMoment = n.sentTime ? toPKT(n.sentTime) : moment();
    setEditingId(n._id);
    setForm({
      title: n.title || "",
      message: n.message || "",
      date: sentMoment.clone(),
      time: sentMoment.clone(),
      runDaily: Boolean(n.runDaily),
      active: Boolean(n.active),
      occupationTypes: n.occupationTypes || [],
      businessNatures: n.businessNatures || [],
    });
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Combine the separate date + time pickers into a PKT-anchored moment so
  // submit / validate / preview all see the SAME instant the backend will
  // fire at — independent of the admin's browser timezone.
  const combinedSentMoment = useMemo(
    () => buildPKTMoment(form.date, form.time),
    [form.date, form.time]
  );

  const validate = useCallback(() => {
    if (!form.title?.trim()) return "Please enter a title";
    if (!form.message?.trim()) return "Please enter a message";
    if (!form.date) return "Please select a date";
    if (!form.time) return "Please select a time";
    // Compare PKT-anchored moments so a UTC-browser admin doesn't get a
    // false-positive "past" rejection (or vice versa).
    if (!form.runDaily && combinedSentMoment && combinedSentMoment.isBefore(nowPKT())) {
      return "Selected date/time is in the past — pick a future time or enable Run Daily";
    }
    return null;
  }, [form, combinedSentMoment]);

  const handleSubmit = useCallback(async () => {
    const error = validate();
    if (error) {
      messageAlert.error(error);
      return;
    }

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      sentTime: toPKTIso(combinedSentMoment),
      runDaily: form.runDaily,
      active: form.active,
      occupationTypes: form.occupationTypes,
      businessNatures: form.businessNatures,
    };

    try {
      setSubmitLoading(true);
      if (editingId) {
        await updateNotificationAdminAlert(
          { ...payload, notificationId: editingId },
          token,
          setSubmitLoading
        );
      } else {
        await createNotificationAdminAlert(payload, token, setSubmitLoading);
      }
      closeModal();
      await fetchNotifications();
    } catch (err) {
      // client already surfaces the error; swallow here so the modal stays open
      console.error("submit failed", err);
    } finally {
      setSubmitLoading(false);
    }
  }, [validate, form, combinedSentMoment, editingId, token, closeModal, fetchNotifications]);

  // ── Row actions ────────────────────────────────────────────────────────
  const handleToggleActive = useCallback(
    async (checked, notification) => {
      if (!token) return;
      setBusy(notification._id, "toggle", true);
      try {
        await updateNotificationAdminAlert(
          {
            notificationId: notification._id,
            active: checked,
            // Include the rest so the backend keeps the record consistent if
            // the mongoose middleware needs it. Everything that isn't sent is
            // left untouched by findByIdAndUpdate.
          },
          token,
          null
        );
        await fetchNotifications();
      } catch (err) {
        console.error(err);
      } finally {
        setBusy(notification._id, "toggle", false);
      }
    },
    [token, fetchNotifications, setBusy]
  );

  const handleDelete = useCallback(
    async (notification) => {
      if (!token) return;
      setBusy(notification._id, "delete", true);
      try {
        await deleteNotificationAdminAlert(notification._id, token, null);
        await fetchNotifications();
      } catch (err) {
        console.error(err);
      } finally {
        setBusy(notification._id, "delete", false);
      }
    },
    [token, fetchNotifications, setBusy]
  );

  // ── Table columns ──────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        width: 220,
        render: (title, row) => (
          <div>
            <div style={{ fontWeight: 600 }}>
              <BellFilled
                style={{
                  color: row.active ? "rgb(251, 180, 56)" : "#bbb",
                  marginRight: 6,
                }}
              />
              {title}
            </div>
            {row.topic && row.topic !== "GENERAL_ANNOUNCEMENT" ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.topic}
              </Text>
            ) : null}
          </div>
        ),
      },
      {
        title: "Message",
        dataIndex: "message",
        key: "message",
        render: (m) => (
          <Tooltip title={m} placement="topLeft">
            <div className="notif-message-cell">{m}</div>
          </Tooltip>
        ),
      },
      {
        title: `Send Time (${PKT_LABEL})`,
        dataIndex: "sentTime",
        key: "sentTime",
        width: 220,
        render: (sentTime, row) => (
          <div>
            <ClockCircleOutlined style={{ marginRight: 6 }} />
            {row.runDaily ? (
              <span>
                Daily at <b>{fmtPKTTime(sentTime)}</b>
              </span>
            ) : (
              <span>{fmtPKT(sentTime)}</span>
            )}
            {row.lastSent ? (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Last sent {fmtPKT(row.lastSent)}
                </Text>
              </div>
            ) : null}
          </div>
        ),
      },
      {
        title: "Mode",
        dataIndex: "runDaily",
        key: "runDaily",
        width: 100,
        render: (runDaily) =>
          runDaily ? (
            <Tag color="geekblue">DAILY</Tag>
          ) : (
            <Tag color="default">ONE-TIME</Tag>
          ),
      },
      {
        title: "Status",
        dataIndex: "active",
        key: "active",
        width: 140,
        render: (active, row) => (
          <Space>
            <Switch
              checked={Boolean(active)}
              loading={isBusy(row._id, "toggle")}
              onChange={(checked) => handleToggleActive(checked, row)}
            />
            <Text type={active ? "success" : "secondary"}>
              {active ? "Active" : "Disabled"}
            </Text>
          </Space>
        ),
      },
      {
        title: "Target",
        key: "target",
        width: 200,
        render: (_, row) => {
          const occ = row.occupationTypes || [];
          const biz = row.businessNatures || [];
          if (occ.length === 0 && biz.length === 0) {
            return <Text type="secondary">All Users</Text>;
          }
          return (
            <div>
              {occ.map((o) => (
                <Tag key={o} color="blue" style={{ marginBottom: 2 }}>
                  {o.replace(/_/g, " ")}
                </Tag>
              ))}
              {biz.map((b) => (
                <Tag key={b} color="green" style={{ marginBottom: 2 }}>
                  {b.replace(/_/g, " ")}
                </Tag>
              ))}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        width: 140,
        render: (_, row) => (
          <Space>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this notification? This cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => handleDelete(row)}
            >
              <Tooltip title="Delete">
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={isBusy(row._id, "delete")}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [openEdit, handleDelete, handleToggleActive, isBusy]
  );

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="notif-page">
      <Card className="notif-header-card" bordered>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={12}>
            <Title level={3} style={{ margin: 0 }}>
              Scheduler Notifications
            </Title>
            <Text type="secondary">
              All times are in <b>{PKT_LABEL}</b>. Daily notifications fire at
              the same clock time every day.
            </Text>
          </Col>
          <Col xs={24} md={12} style={{ textAlign: "right" }}>
            <Space wrap>
              <Input
                placeholder="Search title or message"
                prefix={<SearchOutlined />}
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={fetchNotifications}
                style={{ width: 260 }}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchNotifications}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New Notification
              </Button>
            </Space>
          </Col>
        </Row>

        {schedulerHealth && !schedulerHealth.healthy ? (
          <Alert
            style={{ marginTop: 16 }}
            type="error"
            showIcon
            message="Scheduler is offline — notifications will save but won't fire"
            description={
              schedulerHealth.lastError ||
              (schedulerHealth.redisReady === false
                ? "Redis is unreachable."
                : !schedulerHealth.safetyNetRunning
                ? "The recovery sweep isn't running."
                : "The scheduler queue is unhealthy.")
            }
            action={
              <Button size="small" onClick={checkSchedulerHealth}>
                Re-check
              </Button>
            }
          />
        ) : null}
      </Card>

      <Card className="notif-list-card" bordered>
        <Tabs activeKey={tab} onChange={handleTabChange}>
          <Tabs.TabPane tab="All" key={TAB_KEYS.ALL} />
          <Tabs.TabPane tab="Daily" key={TAB_KEYS.DAILY} />
          <Tabs.TabPane tab="One-time" key={TAB_KEYS.ONE_TIME} />
          <Tabs.TabPane tab="Disabled" key={TAB_KEYS.DISABLED} />
        </Tabs>

        {listError ? (
          <div className="notif-error-banner">{listError}</div>
        ) : null}

        <Table
          rowKey="_id"
          columns={columns}
          dataSource={notifications}
          loading={listLoading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: (
              <Empty
                description="No scheduled notifications in this view"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingId ? "Edit Scheduler Notification" : "New Scheduler Notification"}
        visible={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitLoading}
        okText={editingId ? "Update" : "Schedule"}
        maskClosable={false}
        destroyOnClose
        width={560}
      >
        <Form layout="vertical">
          <Form.Item label="Title" required>
            <Input
              placeholder="Enter notification title"
              value={form.title}
              maxLength={100}
              showCount
              onChange={(e) => updateField("title", e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Message" required>
            <TextArea
              placeholder="Enter the notification body"
              rows={4}
              value={form.message}
              maxLength={500}
              showCount
              onChange={(e) => updateField("message", e.target.value)}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Date (PKT)" required>
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  value={form.date}
                  onChange={(v) => updateField("date", v)}
                  disabledDate={(current) =>
                    // Compare against PKT midnight so a UTC-browser admin
                    // still sees today's PKT date as selectable.
                    !form.runDaily && current && current < nowPKT().startOf("day")
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Time (PKT)" required>
                <TimePicker
                  style={{ width: "100%" }}
                  format="HH:mm"
                  minuteStep={5}
                  value={form.time}
                  onChange={(v) => updateField("time", v)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              <Form.Item label="Run Daily">
                <Space>
                  <Switch
                    checked={form.runDaily}
                    onChange={(v) => updateField("runDaily", v)}
                  />
                  <Text type="secondary">
                    {form.runDaily
                      ? "Fires every day at the chosen time"
                      : "Fires once on the selected date"}
                  </Text>
                </Space>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Active">
                <Space>
                  <Switch
                    checked={form.active}
                    onChange={(v) => updateField("active", v)}
                  />
                  <Text type="secondary">
                    {form.active ? "Enabled & scheduled" : "Saved but disabled"}
                  </Text>
                </Space>
              </Form.Item>
            </Col>
          </Row>

          {combinedSentMoment ? (
            <div className="notif-preview">
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              <span>
                Will fire at{" "}
                <b>{combinedSentMoment.format("DD MMM YYYY, hh:mm A")}</b>{" "}
                ({PKT_LABEL})
              </span>
            </div>
          ) : null}

          <Form.Item label="Target Occupation">
            <Select
              mode="multiple"
              allowClear
              placeholder="All occupations (leave empty for all)"
              value={form.occupationTypes}
              onChange={(v) => updateField("occupationTypes", v)}
            >
              <Select.Option value="TRADER">Trader</Select.Option>
              <Select.Option value="POULTRY_FORMER">Poultry Farmer</Select.Option>
              <Select.Option value="SHOP_KEEPER">Shop Keeper</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Target Business Nature">
            <Select
              mode="multiple"
              allowClear
              placeholder="All business types (leave empty for all)"
              value={form.businessNatures}
              onChange={(v) => updateField("businessNatures", v)}
            >
              <Select.Option value="FEED_MATERIAL">Feed Material</Select.Option>
              <Select.Option value="BROILER_INDUSTRY">Broiler Industry</Select.Option>
              <Select.Option value="EGG_INDUSTRY">Egg Industry</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SchedulerNotification;
